import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { formatDateTime } from "../helper/DateHelper";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import {
  getVisitorBookedList,
  downloadVisitorBookedExcel,
  sendBulkVisitorSMS
} from "../Apis/VisitorEnquiryAPIs";
import { getExposList } from "../Apis/ExpoAPIs";
import { toast } from "react-toastify";
import TablePagination from "./TablePagination";
import usePermissions from "../hook/userPermission";

const VisitorBookingListLayer = () => {
  const { hasPermission } = usePermissions();
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // Dropdown data
  const [expoOptions, setExpoOptions] = useState([]);

  // Filters
  const [selectedExpo, setSelectedExpo] = useState(null);
  const [selectedAttendance, setSelectedAttendance] = useState(null);

  // Bulk SMS & WhatsApp Selection
  const [selectedVisitorIds, setSelectedVisitorIds] = useState([]);
  const [sendingSMS, setSendingSMS] = useState(false);

  const handleSendBulkSMS = async () => {
    if (selectedVisitorIds.length === 0) {
      toast.warning("Please select at least one visitor to send SMS");
      return;
    }

    const selectedVisitors = visitors.filter((v) =>
      selectedVisitorIds.includes(v._id || v.id)
    );
    const numbers = selectedVisitors
      .map((v) => v.mobileNo || v.phone)
      .filter(Boolean);

    if (numbers.length === 0) {
      toast.warning("No valid mobile numbers found for selected visitors");
      return;
    }

    try {
      setSendingSMS(true);
      const res = await sendBulkVisitorSMS({ numbers });
      if (res?.status) {
        toast.success(res.response?.message || `SMS sent successfully to ${numbers.length} visitor(s)!`);
        setSelectedVisitorIds([]);
      } else {
        toast.error(res?.response?.message || "Failed to send SMS");
      }
    } catch (err) {
      console.error("Error sending SMS:", err);
      toast.error("An error occurred while sending SMS");
    } finally {
      setSendingSMS(false);
    }
  };

  const attendanceOptions = [
    { value: "Attended", label: "Attended" },
    { value: "Not Attended", label: "Not Attended" },
  ];

  // Load expo options on mount
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const expoRes = await getExposList({ limit: 1000 });
        if (expoRes?.status) {
          const expos = expoRes.response?.data || expoRes.response || [];
          setExpoOptions(
            expos.map((e) => ({
              value: e._id || e.id,
              label: e.expoName || e.name,
            }))
          );
        }
      } catch (err) {
        console.error("Failed to load filter dropdowns:", err);
      }
    };
    loadDropdowns();
  }, []);

  const fetchVisitors = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        expoId: selectedExpo?.value || undefined,
        attendance: selectedAttendance?.value || undefined,
      };

      const result = await getVisitorBookedList(params);
      if (result.status) {
        const data = result.response;
        setVisitors(data.data);
        setTotalItems(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching visitors:", error);
      toast.error("Failed to fetch visitors");
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, selectedExpo, selectedAttendance]);

  const handleExportExcel = async () => {
    try {
      const params = {
        search: searchTerm || undefined,
        expoId: selectedExpo?.value || undefined,
        attendance: selectedAttendance?.value || undefined,
      };

      const result = await downloadVisitorBookedExcel(params);

      if (result.status) {
        const url = window.URL.createObjectURL(new Blob([result.response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Visitor_Booked_List_${new Date().toLocaleDateString()}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Excel downloaded successfully");
      }
    } catch (error) {
      console.error("Error exporting excel:", error);
      toast.error("Failed to export excel");
    }
  };

  useEffect(() => {
    fetchVisitors();
  }, [fetchVisitors]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    setSelectedExpo(null);
    setSelectedAttendance(null);
    setSearchTerm("");
    setCurrentPage(0);
  };


  return (
    <div className="card h-100 p-0 radius-12 border-0 shadow-sm">
      {/* Header */}
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{
          background:
            "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)",
        }}
      >
        <h6 className="text-white mb-0 fw-bold">All Visitor Booking List</h6>
      </div>

      {/* Filter Bar */}
      <div
        className="px-24 py-16 border-bottom"
        style={{ backgroundColor: "#fafafa" }}
      >
        <div className="d-flex align-items-end flex-wrap gap-3">

          {/* Expo Name Dropdown */}
          <div style={{ minWidth: "220px", flex: "1 1 220px", maxWidth: "280px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Expo Name
            </label>
            <Select
              options={expoOptions}
              value={selectedExpo}
              onChange={(val) => {
                setSelectedExpo(val);
                setCurrentPage(0);
              }}
              isClearable
              placeholder="All Expos"
              styles={selectStyles()}
            />
          </div>

          {/* Attendance Filter Dropdown */}
          <div style={{ minWidth: "180px", flex: "1 1 180px", maxWidth: "240px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Attendance
            </label>
            <Select
              options={attendanceOptions}
              value={selectedAttendance}
              onChange={(val) => {
                setSelectedAttendance(val);
                setCurrentPage(0);
              }}
              isClearable
              placeholder="All Attendance"
              styles={selectStyles()}
            />
          </div>

          {/* Search */}
          <div style={{ minWidth: "200px", flex: "1 1 200px", maxWidth: "260px" }}>
            <label
              className="form-label text-xs fw-bold text-secondary-light uppercase mb-1"
              style={{ letterSpacing: "0.5px" }}
            >
              Search
            </label>
            <div className="position-relative">
              <input
                type="text"
                className="form-control radius-8 ps-40"
                style={{ height: "44px" }}
                placeholder="Name / Company..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
              <Icon
                icon="ion:search-outline"
                className="position-absolute top-50 translate-middle-y text-secondary-light"
                style={{ fontSize: "18px", left: "12px" }}
              />
            </div>
          </div>

          {/* Clear Filters Button */}
          <div className="d-flex align-items-end">
            <button
              className="btn btn-outline-danger h-44-px px-20 radius-8 fw-bold d-flex align-items-center justify-content-center gap-2"
              onClick={handleClearFilters}
              title="Clear Filters"
            >
              <Icon icon="solar:restart-bold" className="text-xl" />
              Reset
            </button>
          </div>

        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive border radius-12 shadow-sm mb-4">
          <table className="table bordered-table sm-table mb-0 text-nowrap" style={{ minWidth: '1550px' }}>
            <thead>
              <tr
                style={{
                  background: "linear-gradient(135deg, #bb0007, #ed1c24)",
                }}
              >
                <th scope="col" style={{ width: "45px" }} className="px-16 text-center">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={
                      visitors.length > 0 &&
                      visitors.every((v) => selectedVisitorIds.includes(v._id || v.id))
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedVisitorIds(visitors.map((v) => v._id || v.id));
                      } else {
                        setSelectedVisitorIds([]);
                      }
                    }}
                  />
                </th>
                <th
                  scope="col"
                  style={{ color: "white", fontWeight: "bold" }}
                  className="px-24"
                >
                  S.No
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Exhibition Name
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Visitor Name
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Company
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Mobile Number
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Email
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Attendance
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Attended Date & Time
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Known Source
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Source
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Venue
                </th>
                <th scope="col" style={{ color: "white", fontWeight: "bold" }}>
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="text-center py-48">
                    <div
                      className="spinner-border text-danger"
                      role="status"
                    ></div>
                    <div className="mt-12 text-secondary-light fw-medium">
                      Loading visitors...
                    </div>
                  </td>
                </tr>
              ) : visitors.length > 0 ? (
                visitors.map((item, index) => (
                  <tr key={item._id} className="hover-bg-light transition-all">
                    <td className="px-16 text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedVisitorIds.includes(item._id || item.id)}
                        onChange={(e) => {
                          const id = item._id || item.id;
                          if (e.target.checked) {
                            setSelectedVisitorIds((prev) => [...prev, id]);
                          } else {
                            setSelectedVisitorIds((prev) => prev.filter((x) => x !== id));
                          }
                        }}
                      />
                    </td>
                    <td className="px-24 fw-medium text-secondary-light">
                      {currentPage * itemsPerPage + index + 1}
                    </td>
                    <td>
                      {item.expoId?.expoName || item.expoId?.name || "-"}
                    </td>
                    <td>
                      <div>{item.name}</div>
                    </td>
                    <td>
                      {item.companyName || item.company || "-"}
                    </td>
                    <td className="text-secondary-light fw-medium">
                      {item.mobileNo}
                    </td>
                    <td className="text-secondary-light">{item.email || "-"}</td>
                    <td>
                      <span
                        className={`badge ${
                          item.attendance
                            ? "bg-success-subtle text-success"
                            : "bg-warning-subtle text-warning"
                        } fw-bold`}
                      >
                        {item.attendance ? "✓ Attended" : "⏱ Not Attended"}
                      </span>
                    </td>
                    <td className="text-secondary-light">
                      {item.attendance && (item.attendedDate || item.attendedTime)
                        ? `${item.attendedDate || ""} ${item.attendedTime || ""}`.trim()
                        : "-"}
                    </td>
                    <td className="text-secondary-light">
                      {item.knowAboutExhibition && item.knowAboutExhibition !== "-" ? (
                        <span className="badge bg-info-subtle text-info fw-bold">
                          {item.knowAboutExhibition}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="text-secondary-light">{item.source || "-"}</td>
                    <td className="text-secondary-light">
                      {item.expoId?.venue || "-"}
                    </td>
                    <td className="text-secondary-light">
                      {formatDateTime(item.createdAt || item.registerDate)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="text-center py-48">
                    <div className="text-secondary-light fw-medium">
                      No visitors found matching your filters.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-24">
          <div className="d-flex align-items-center gap-2">
            {hasPermission("visitor_booking_list", "view") && (
              <button
                className="btn d-flex align-items-center gap-2 px-20 py-10 radius-8 shadow-sm fw-bold border-0"
                style={{ backgroundColor: "#22c55e", color: "white" }}
                onClick={handleExportExcel}
              >
                <Icon icon="lucide:file-spreadsheet" className="text-xl" />
                Export Excel
              </button>
            )}
            <button
              className="btn d-flex align-items-center gap-2 px-20 py-10 radius-8 shadow-sm fw-bold border-0"
              style={{ backgroundColor: "#25d366", color: "white" }}
              onClick={() => {
                if (selectedVisitorIds.length === 0) {
                  toast.warning("Please select at least one visitor");
                  return;
                }
                toast.info("WhatsApp API integration is in progress");
              }}
              title="Send WhatsApp Message"
            >
              <Icon icon="ic:baseline-whatsapp" className="text-xl" />
              WhatsApp {selectedVisitorIds.length > 0 ? `(${selectedVisitorIds.length})` : ""}
            </button>

            <button
              className="btn d-flex align-items-center gap-2 px-20 py-10 radius-8 shadow-sm fw-bold border-0"
              style={{ backgroundColor: "#0284c7", color: "white" }}
              onClick={handleSendBulkSMS}
              disabled={sendingSMS}
              title="Send Bulk SMS"
            >
              <Icon icon="solar:chat-round-line-bold" className="text-xl" />
              {sendingSMS ? "Sending SMS..." : `Send SMS ${selectedVisitorIds.length > 0 ? `(${selectedVisitorIds.length})` : ""}`}
            </button>
          </div>

          <TablePagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / itemsPerPage)}
            onPageChange={handlePageChange}
            rowsPerPage={itemsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            totalRecords={totalItems}
          />
        </div>
      </div>
    </div>
  );
};

export default VisitorBookingListLayer;
